var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Big from "big.js";
import { isKeyringPair } from "@polkadot/api/util";
import { currencyIdToMonetaryCurrency, encodeBtcAddress, FIXEDI128_SCALING_FACTOR, getCurrencyIdentifier, isForeignAsset, isLendToken, PERMILL_BASE, } from ".";
import { IssueStatus, RedeemStatus } from "../types/requestTypes";
import { newMonetaryAmount } from "../utils";
import { stringToU8a, u8aConcat } from "@polkadot/util";
const EMPTY_H256 = new Uint8Array(32);
const MOD_PREFIX = stringToU8a("modl");
export function intoAccountTruncating(api, palletId) {
    return api.createType("AccountId", u8aConcat(MOD_PREFIX, palletId, EMPTY_H256));
}
/**
 * Converts endianness of a Uint8Array
 * @param bytes Uint8Array, to be converted LE<>BE
 */
export function reverseEndianness(bytes) {
    let offset = bytes.length;
    for (let index = 0; index < bytes.length; index += bytes.length) {
        offset--;
        for (let x = 0; x < offset; x++) {
            const b = bytes[index + x];
            bytes[index + x] = bytes[index + offset];
            bytes[index + offset] = b;
            offset--;
        }
    }
    return bytes;
}
function isHexPrefixed(str) {
    return str.slice(0, 2) === "0x";
}
/**
 * Remove the `0x` hex prefix if present
 * @param str
 */
export function stripHexPrefix(str) {
    return isHexPrefixed(str) ? str.slice(2) : str;
}
/**
 * Ensure the `0x` hex prefix is present
 * @param str
 **/
export function addHexPrefix(str) {
    return isHexPrefixed(str) ? str : "0x" + str;
}
/**
 * Ensure a hash value is an encoded H256
 * @param api The polkadot API promise used to encode if necessary
 * @param hash The either H256 or string encoded hash
 **/
export function ensureHashEncoded(api, hash) {
    if (typeof hash === "string") {
        return api.createType("H256", addHexPrefix(hash));
    }
    else {
        return hash;
    }
}
/**
 * Reverse the endianness of the given hex string
 * @dev Will remove `0x` prefix if present
 * @param hex
 */
export function reverseEndiannessHex(hex) {
    const arr = stripHexPrefix(hex).match(/.{1,2}/g) || [];
    const bytes = new Uint8Array(arr.map((byte) => parseInt(byte, 16)));
    return reverseEndianness(bytes).reduce((str, byte) => str + byte.toString(16).padStart(2, "0"), "");
}
/**
 * Converts a Uint8Array to string
 * @dev Will remove `0x` prefix if present
 * @param bytes
 */
export function uint8ArrayToString(bytes) {
    return stripHexPrefix(bytes.toString()).split("").join("");
}
export function decodeFixedPointType(x) {
    const xBig = new Big(x.toString());
    const scalingFactor = new Big(Math.pow(10, FIXEDI128_SCALING_FACTOR));
    return xBig.div(scalingFactor);
}
/**
 * Convert Bytes to a string. Will remove `0x` prefix if present.
 * @param bytes Bytes to decode
 * @returns the decoded string
 */
export function decodeBytesAsString(bytes) {
    return Buffer.from(stripHexPrefix(bytes.toString()), "hex").toString();
}
export function encodeUnsignedFixedPoint(api, x) {
    const scalingFactor = new Big(Math.pow(10, FIXEDI128_SCALING_FACTOR));
    // If there are any decimals left after scaling up by the scaling factor,
    // the resulting FixedU128 will be wrong. As such, trim any decimals.
    const xScaled = x.mul(scalingFactor).round(0, 0);
    return api.createType("FixedU128", xScaled.toFixed());
}
export function storageKeyToNthInner(s, n = 0) {
    return s.args[n];
}
export function parseSystemVault(api, vault, wrappedCurrency, collateralCurrency) {
    return __awaiter(this, void 0, void 0, function* () {
        return {
            toBeIssuedTokens: newMonetaryAmount(vault.toBeIssuedTokens.toString(), wrappedCurrency),
            issuedTokens: newMonetaryAmount(vault.issuedTokens.toString(), wrappedCurrency),
            toBeRedeemedTokens: newMonetaryAmount(vault.toBeRedeemedTokens.toString(), wrappedCurrency),
            collateral: newMonetaryAmount(vault.collateral.toString(), collateralCurrency),
            currencyPair: {
                collateralCurrency: yield currencyIdToMonetaryCurrency(api, vault.currencyPair.collateral),
                wrappedCurrency: yield currencyIdToMonetaryCurrency(api, vault.currencyPair.wrapped),
            },
        };
    });
}
export function newAccountId(api, accountId) {
    return api.createType("AccountId", accountId);
}
export function newVaultId(api, accountId, collateralCurrency, wrappedCurrency) {
    const parsedAccountId = newAccountId(api, accountId);
    const vaultCurrencyPair = newVaultCurrencyPair(api, collateralCurrency, wrappedCurrency);
    return api.createType("InterbtcPrimitivesVaultId", { account_id: parsedAccountId, currencies: vaultCurrencyPair });
}
export function decodeRpcVaultId(api, vaultId) {
    return __awaiter(this, void 0, void 0, function* () {
        const [collateralCcy, wrappedCcy] = yield Promise.all([
            currencyIdToMonetaryCurrency(api, vaultId.currencies.collateral),
            currencyIdToMonetaryCurrency(api, vaultId.currencies.wrapped),
        ]);
        return newVaultId(api, vaultId.account_id.toString(), collateralCcy, wrappedCcy);
    });
}
export function newVaultCurrencyPair(api, collateralCurrency, wrappedCurrency) {
    const collateralCurrencyId = newCurrencyId(api, collateralCurrency);
    const wrappedCurrencyId = newCurrencyId(api, wrappedCurrency);
    return api.createType("InterbtcPrimitivesVaultCurrencyPair", {
        collateral: collateralCurrencyId,
        wrapped: wrappedCurrencyId,
    });
}
export function newCurrencyId(api, currency) {
    const identifier = getCurrencyIdentifier(currency);
    return api.createType("InterbtcPrimitivesCurrencyId", identifier);
}
export function newForeignAssetId(api, id) {
    return api.createType("u32", id);
}
export function addressOrPairAsAccountId(api, addyOrpair) {
    const addressString = isKeyringPair(addyOrpair) ? addyOrpair.address : addyOrpair.toString();
    return newAccountId(api, addressString);
}
export function parseReplaceRequest(api, req, network, wrappedCurrency, id) {
    return __awaiter(this, void 0, void 0, function* () {
        const collateralCurrency = yield currencyIdToMonetaryCurrency(api, req.oldVault.currencies.collateral);
        return {
            id: stripHexPrefix(id.toString()),
            btcAddress: encodeBtcAddress(req.btcAddress, network),
            newVault: req.newVault,
            oldVault: req.oldVault,
            amount: newMonetaryAmount(req.amount.toString(), wrappedCurrency),
            griefingCollateral: newMonetaryAmount(req.griefingCollateral.toString(), collateralCurrency),
            collateral: newMonetaryAmount(req.collateral.toString(), collateralCurrency),
            acceptTime: req.acceptTime.toNumber(),
            period: req.period.toNumber(),
            btcHeight: req.btcHeight.toNumber(),
            status: req.status,
        };
    });
}
export function parseIssueRequest(api, vaultsAPI, req, network, id) {
    return __awaiter(this, void 0, void 0, function* () {
        const status = req.status.isCompleted
            ? IssueStatus.Completed
            : req.status.isCancelled
                ? IssueStatus.Cancelled
                : IssueStatus.PendingWithBtcTxNotFound;
        const collateralCurrency = yield currencyIdToMonetaryCurrency(api, req.vault.currencies.collateral);
        return {
            id: stripHexPrefix(id.toString()),
            creationBlock: req.opentime.toNumber(),
            vaultWrappedAddress: encodeBtcAddress(req.btcAddress, network),
            vaultId: req.vault,
            userParachainAddress: req.requester.toString(),
            vaultWalletPubkey: req.btcPublicKey.toString(),
            bridgeFee: newMonetaryAmount(req.fee.toString(), vaultsAPI.getWrappedCurrency()),
            wrappedAmount: newMonetaryAmount(req.amount.toString(), vaultsAPI.getWrappedCurrency()),
            griefingCollateral: newMonetaryAmount(req.griefingCollateral.toString(), collateralCurrency),
            status,
            period: req.period.toNumber(),
        };
    });
}
export function parseRedeemRequestStatus(req, redeemPeriod, activeBlockCount) {
    const primStatus = req.status;
    // check obvious final states first
    if (primStatus.isCompleted) {
        return RedeemStatus.Completed;
    }
    if (primStatus.isReimbursed) {
        return RedeemStatus.Reimbursed;
    }
    if (primStatus.isRetried) {
        return RedeemStatus.Retried;
    }
    // now check if pending is actually expired
    const maxPeriod = Math.max(req.period.toNumber(), redeemPeriod);
    const openTime = req.opentime.toNumber();
    if (openTime + maxPeriod < activeBlockCount) {
        return RedeemStatus.Expired;
    }
    return RedeemStatus.PendingWithBtcTxNotFound;
}
export function parseRedeemRequest(api, vaultsAPI, req, network, id, redeemPeriod, activeBlockCount) {
    return __awaiter(this, void 0, void 0, function* () {
        const status = parseRedeemRequestStatus(req, redeemPeriod, activeBlockCount);
        const collateralCurrency = yield currencyIdToMonetaryCurrency(api, req.vault.currencies.collateral);
        return {
            id: stripHexPrefix(id.toString()),
            userParachainAddress: req.redeemer.toString(),
            amountBTC: newMonetaryAmount(req.amountBtc.toString(), vaultsAPI.getWrappedCurrency()),
            collateralPremium: newMonetaryAmount(req.premium.toString(), collateralCurrency),
            bridgeFee: newMonetaryAmount(req.fee.toString(), vaultsAPI.getWrappedCurrency()),
            btcTransferFee: newMonetaryAmount(req.transferFeeBtc.toString(), vaultsAPI.getWrappedCurrency()),
            creationBlock: req.opentime.toNumber(),
            vaultId: req.vault,
            userBTCAddress: encodeBtcAddress(req.btcAddress, network),
            status,
            period: req.period.toNumber(),
        };
    });
}
export function convertMoment(moment) {
    return new Date(moment.toNumber());
}
export function unwrapRawExchangeRate(option) {
    return option.isSome ? option.value : undefined;
}
export function encodeVaultId(api, id) {
    return __awaiter(this, void 0, void 0, function* () {
        const [wrappedCurrency, collateralCurrency] = yield Promise.all([
            currencyIdToMonetaryCurrency(api, id.currencies.wrapped),
            currencyIdToMonetaryCurrency(api, id.currencies.collateral),
        ]);
        const wrappedId = isForeignAsset(wrappedCurrency)
            ? wrappedCurrency.foreignAsset.id.toString()
            : isLendToken(wrappedCurrency)
                ? wrappedCurrency.lendToken.id
                : wrappedCurrency.ticker;
        const collateralId = isForeignAsset(collateralCurrency)
            ? collateralCurrency.foreignAsset.id.toString()
            : isLendToken(collateralCurrency)
                ? collateralCurrency.lendToken.id.toString()
                : collateralCurrency.ticker;
        return `${id.accountId.toString()}-${wrappedId}-${collateralId}`;
    });
}
export function queryNominationsMap(api, map, vaultId) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const [entryVaultId, entryNonce] of map.entries()) {
            if ((yield encodeVaultId(api, entryVaultId)) === (yield encodeVaultId(api, vaultId))) {
                return entryNonce;
            }
        }
        return undefined;
    });
}
export function getSS58Prefix(api) {
    var _a;
    return Number((_a = api.registry.chainSS58) === null || _a === void 0 ? void 0 : _a.toString());
}
export function decodePermill(amount, inPercentage = false) {
    const decodedInDecimals = Big(amount.toString()).div(PERMILL_BASE);
    if (inPercentage) {
        return decodedInDecimals.mul(100);
    }
    return decodedInDecimals;
}
export function decodeNumberOrHex(value) {
    if (value.isHex) {
        return Big(value.asHex.toBigInt().toString());
    }
    return Big(value.asNumber.toString());
}
export function monetaryAmountToRawString(amount) {
    return amount.toString(true);
}
export function newExtrinsicStatus(api, type) {
    return api.createType("ExtrinsicStatus", type);
}
export function bufferToHexString(buffer) {
    return `0x${buffer.toString("hex")}`;
}
//# sourceMappingURL=encoding.js.map